/**
 * Das nachgetragene Schliessen — reine Entscheidung, kein IO.
 *
 * Warum es diese Datei gibt: die Erfassung schreibt ihr `project_close` in
 * dem Augenblick, in dem der Anwender das Projekt verlaesst. Auf einem
 * Mobilgeraet gibt es diesen Augenblick oft nicht. Die Seite wird
 * weggewischt, das Betriebssystem raeumt sie im Hintergrund ab, der Akku ist
 * leer — kein Ereignis, kein Netz, keine Zeile. Gemessen an der Praxis ist
 * das kein Sonderfall: bei einem Aussendienst-Konto standen 14 von 19
 * Sitzungen dauerhaft als „offen", weil ausschliesslich der Zurueck-Knopf ein
 * Schliessen erzeugt hat.
 *
 * Deshalb fuehrt `userActivityService` ein Journal im localStorage (nicht
 * sessionStorage — genau der Kaltstart soll es ja finden): solange ein
 * Projekt offen ist, liegt dort, WAS offen ist und WANN es zuletzt gelebt
 * hat. Beim naechsten App-Start wird ein liegengebliebener Eintrag
 * nachgetragen.
 *
 * ─── Die Regel bleibt: Dauer wird NIE geschaetzt ───────────────────────────
 * Nachgetragen wird nur mit einem Zeitpunkt, den die App WIRKLICH gesehen
 * hat, niemals mit „jetzt" und niemals mit einem gerechneten Wert. Drei
 * Quellen, in dieser Reihenfolge:
 *
 *   1. `beaconAt`  — das Entladen hat noch gefeuert und den keepalive-Beacon
 *                    abgeschickt; ob er ankam, weiss der Client nicht. Grund
 *                    bleibt 'unload', der Zeitpunkt ist exakt.
 *   2. `hiddenAt`  — die Seite ging in den Hintergrund (App-Wechsel,
 *                    Bildschirm aus) und ist von dort nicht zurueckgekehrt.
 *                    Grund 'hidden', der Zeitpunkt ist exakt.
 *   3. `lastSeenAt` — die Seite starb ohne jedes Ereignis (Absturz, Kill im
 *                    Vordergrund). Uebrig bleibt der letzte Herzschlag, der
 *                    bis zu HEARTBEAT_MS alt ist. Deshalb traegt genau dieser
 *                    Fall einen EIGENEN Grund 'recovered': die Dauer ist
 *                    gemessen, aber am Ende bis zu einem Herzschlag zu kurz,
 *                    und das darf die Auswertung nicht mit einer exakt
 *                    beobachteten verwechseln.
 *
 * Was nicht plausibel ist, wird verworfen statt zurechtgebogen — ein
 * Schliessen vor dem Oeffnen oder in der Zukunft waere eine erfundene Zahl,
 * und eine offen stehende Sitzung ist der ehrlichere Befund.
 */

import type { UserActivityCloseReason } from './userActivityTypes';

/** Der Journaleintrag. Genau EIN offenes Projekt, wie die Wache im Dienst. */
export interface PendingActivityClose {
    /** Formatstand. Ein unbekannter Wert wird verworfen statt geraten. */
    v: 1;
    /**
     * Wem die Sitzung gehoert. Der Nachtrag laeuft ueber einen gewoehnlichen
     * Insert und traegt damit `auth.uid()` des GERADE angemeldeten Nutzers —
     * ein fremder Eintrag wuerde also unter der falschen Person landen. Nur
     * deshalb steht die Benutzer-ID hier.
     */
    userId: string;
    /**
     * Die Sitzung des Oeffnens. Die Paarung der Auswertung laeuft
     * ausschliesslich innerhalb einer `session_id` — der Nachtrag MUSS die
     * alte tragen, nicht die des laufenden Starts.
     */
    sessionId: string;
    projectId: string;
    projectNumber: string | null;
    projectName: string | null;
    /** `occurred_at` des zugehoerigen Oeffnens — Plausibilitaetsgrenze. */
    openedAt: string;
    /** Letzter Herzschlag, solange die Seite lebte. */
    lastSeenAt: string;
    /** Wechsel in den Hintergrund, `null` = im Vordergrund. */
    hiddenAt: string | null;
    /** Ein Beacon ist beim Entladen hinausgegangen — Ankunft ungewiss. */
    beaconAt: string | null;
}

export type RecoveryDecision =
    | { kind: 'flush'; occurredAt: string; reason: UserActivityCloseReason }
    /** `keep`: nicht schreiben, aber auch nicht wegwerfen (fremder Nutzer). */
    | { kind: 'keep'; why: 'fremder-nutzer' }
    | { kind: 'drop'; why: 'unbrauchbar' };

const istIso = (wert: unknown): wert is string =>
    typeof wert === 'string' && !!wert && Number.isFinite(Date.parse(wert));

const istText = (wert: unknown): wert is string => typeof wert === 'string' && !!wert;

const optText = (wert: unknown): string | null => (typeof wert === 'string' && wert ? wert : null);

const optIso = (wert: unknown): string | null => (istIso(wert) ? wert : null);

export const serializePending = (p: PendingActivityClose): string => JSON.stringify(p);

/**
 * Strenge Lesung. Ein Eintrag, dem auch nur ein Pflichtfeld fehlt, ist kein
 * halber Eintrag, sondern keiner: aus ihm liesse sich nur eine erfundene
 * Sitzung bauen.
 */
export const parsePending = (raw: string | null | undefined): PendingActivityClose | null => {
    if (!raw) return null;
    let roh: any;
    try {
        roh = JSON.parse(raw);
    } catch {
        return null;
    }
    return parsePendingObject(roh);
};

const parsePendingObject = (roh: any): PendingActivityClose | null => {
    if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return null;
    if (roh.v !== 1) return null;
    if (!istText(roh.userId) || !istText(roh.sessionId) || !istText(roh.projectId)) return null;
    if (!istIso(roh.openedAt) || !istIso(roh.lastSeenAt)) return null;
    return {
        v: 1,
        userId: roh.userId,
        sessionId: roh.sessionId,
        projectId: roh.projectId,
        projectNumber: optText(roh.projectNumber),
        projectName: optText(roh.projectName),
        openedAt: roh.openedAt,
        lastSeenAt: roh.lastSeenAt,
        hiddenAt: optIso(roh.hiddenAt),
        beaconAt: optIso(roh.beaconAt),
    };
};

/**
 * Was mit einem liegengebliebenen Eintrag geschieht.
 *
 * Der fremde Nutzer wird BEHALTEN und nicht verworfen: der Eintrag ist ja
 * gueltig, nur darf ihn niemand ausser seinem Eigentuemer schreiben. Auf
 * einem geteilten Geraet meldet der sich frueher oder spaeter wieder an und
 * traegt ihn dann selbst nach. Weggeworfen wird nur, was unbrauchbar ist.
 */
export const decideRecovery = (
    p: PendingActivityClose,
    currentUserId: string,
    nowIso: string,
): RecoveryDecision => {
    if (!currentUserId || p.userId !== currentUserId) return { kind: 'keep', why: 'fremder-nutzer' };

    const quelle: Array<[string | null, UserActivityCloseReason]> = [
        [p.beaconAt, 'unload'],
        [p.hiddenAt, 'hidden'],
        [p.lastSeenAt, 'recovered'],
    ];
    const treffer = quelle.find(([wert]) => istIso(wert));
    if (!treffer) return { kind: 'drop', why: 'unbrauchbar' };
    const occurredAt = treffer[0] as string;
    const reason = treffer[1];

    const ms = Date.parse(occurredAt);
    const auf = Date.parse(p.openedAt);
    const jetzt = Date.parse(nowIso);
    // Vor dem Oeffnen oder nach dem Jetzt: eine verstellte Uhr. Eine negative
    // oder in die Zukunft reichende Dauer waere schlimmer als die Luecke.
    if (ms < auf) return { kind: 'drop', why: 'unbrauchbar' };
    if (Number.isFinite(jetzt) && ms > jetzt) return { kind: 'drop', why: 'unbrauchbar' };

    return { kind: 'flush', occurredAt, reason };
};

/**
 * ─── Das Journal hat MEHRERE Plaetze, einen je Browser-Sitzung ─────────────
 *
 * Bis 07.09.2026 gab es genau EINEN Eintrag. Das reichte, solange ein Geraet
 * eine Sitzung nach der anderen fuehrt — und genau das tut ein Telefon
 * nicht: die Seite stirbt im Hintergrund, der naechste Kaltstart oeffnet ein
 * anderes Projekt, `writePending` ueberschreibt den einen Platz, und der
 * Start danach hat nichts mehr nachzutragen. Flottenweit standen so 19
 * Sitzungen seit dem 26.08. offen, obwohl die Seite nachweislich noch
 * Minuten gelebt hatte. Deshalb je Sitzung ein Platz; `sessionId` ist der
 * Schluessel, ein zweiter Eintrag derselben Sitzung ersetzt den ersten.
 * Gedeckelt, damit ein Geraet, das nie nachtragen kann, den Speicher nicht
 * fuellt — es fallen die aeltesten Oeffnungen weg, nie die juengsten.
 */
export const PENDING_LIST_MAX = 20;

export const parsePendingList = (raw: string | null | undefined): PendingActivityClose[] => {
    if (!raw) return [];
    let roh: any;
    try {
        roh = JSON.parse(raw);
    } catch {
        return [];
    }
    // Ein Alt-Journal (v1, ein Objekt) wird als Liste mit einem Eintrag gelesen.
    const liste: any[] = Array.isArray(roh) ? roh : [roh];
    const out: PendingActivityClose[] = [];
    for (const e of liste) {
        const p = parsePendingObject(e);
        if (p && !out.some(x => x.sessionId === p.sessionId)) out.push(p);
    }
    return out;
};

export const serializePendingList = (liste: PendingActivityClose[]): string => JSON.stringify(liste);

/** Eintrag der Sitzung ersetzen oder anhaengen; ueber dem Deckel fliegen die aeltesten Oeffnungen. */
export const upsertPending = (
    liste: PendingActivityClose[],
    eintrag: PendingActivityClose,
    max: number = PENDING_LIST_MAX,
): PendingActivityClose[] => {
    const rest = liste.filter(e => e.sessionId !== eintrag.sessionId);
    const out = [...rest, eintrag];
    if (out.length <= max) return out;
    return [...out].sort((a, b) => Date.parse(a.openedAt) - Date.parse(b.openedAt)).slice(out.length - max);
};

/**
 * Genau DIESEN Eintrag entfernen — Sitzung UND Oeffnungszeit muessen passen.
 * Nur die Sitzung zu vergleichen waere gefaehrlich: der Nachtrag laeuft
 * asynchron, und waehrend er unterwegs ist, kann dieselbe Sitzung (F5,
 * Nav-Restore) schon wieder ein Projekt geoeffnet und ihren Platz neu
 * beschrieben haben. Der wuerde sonst mit dem alten Eintrag weggeraeumt.
 */
export const removePending = (
    liste: PendingActivityClose[],
    sessionId: string,
    openedAt?: string,
): PendingActivityClose[] =>
    liste.filter(e => !(e.sessionId === sessionId && (openedAt === undefined || e.openedAt === openedAt)));
